import { useState } from 'react'
import { toast } from 'react-hot-toast'

import Router from 'next/router'

import { useSignOutUserMutation } from '@graphql/react-apollo'

import { useEventTracking } from '@web-app/context/EventTrackingContext'
import { broadcastSignOut } from '@web-app/lib/auth/broadcast'
import { logout } from '@web-app/lib/auth/client'
import { SIGN_IN_PATH } from '@web-app/utils/auth/constants'

interface SignOutOptions {
  // false when the server already revoked the session (account closure).
  revoke?: boolean
  successToast?: string
}

// Revoke server-side, end the local session, land on sign-in.
export function useSignOut(comp: string) {
  const { trackEvent } = useEventTracking()
  const [signOutUser] = useSignOutUserMutation()
  const [isSigningOut, setIsSigningOut] = useState(false)

  const signOut = async ({
    revoke = true,
    successToast = 'Successfully logged out!',
  }: SignOutOptions = {}) => {
    if (isSigningOut) return
    setIsSigningOut(true)
    trackEvent('view_sign_out', { comp })

    // Awaited so the Bearer flies before the cookie dies; its failure never
    // blocks the local sign-out.
    if (revoke) {
      await signOutUser({
        variables: { input: {} },
        context: { errorToast: false },
      }).catch(() => undefined)
    }

    // The GraphQL call only revokes the backend session; this ends the
    // HttpOnly cookie, ordered after any in-flight mint by the client's lock.
    const outcome = await logout()
    if (outcome.kind !== 'signed-out') {
      // The httpOnly cookie only dies via our server — offline, it can't.
      toast.error('Could not log out — check your connection.')
      setIsSigningOut(false)
      return
    }
    broadcastSignOut()
    toast.success(successToast)
    Router.push(SIGN_IN_PATH)
  }

  return { signOut, isSigningOut }
}
